import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { DollarSign, TrendingUp, BarChart2, Zap, PlusCircle } from 'lucide-react';

const SustainableFinance = () => {
  const [financialMetrics, setFinancialMetrics] = useState([
    { name: "Green Bonds", value: 500, unit: "M", change: 15, history: [300, 350, 400, 450, 500] },
    { name: "Sustainable Investments", value: 1200, unit: "M", change: 8, history: [900, 1000, 1050, 1100, 1200] },
    { name: "ESG-Linked Loans", value: 300, unit: "M", change: 5, history: [250, 270, 280, 290, 300] },
    { name: "Renewable Energy Investments", value: 750, unit: "M", change: 20, history: [500, 550, 600, 650, 750] },
  ]);

  const [newGoal, setNewGoal] = useState({ name: '', target: '' });
  const [showGoalForm, setShowGoalForm] = useState(false);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const totalValue = financialMetrics.reduce((sum, metric) => sum + metric.value, 0);

  const handleAddGoal = () => {
    if (newGoal.name && newGoal.target) {
      setFinancialMetrics([...financialMetrics, { ...newGoal, value: 0, unit: "M", change: 0, history: [0] }]);
      setNewGoal({ name: '', target: '' });
      setShowGoalForm(false);
    }
  };

  const trendData = financialMetrics[0].history.map((_, index) => ({
    year: 2019 + index,
    ...financialMetrics.reduce((acc, metric) => {
      acc[metric.name] = metric.history[index];
      return acc;
    }, {})
  }));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Sustainable Finance</h1>
        <Button><BarChart2 className="mr-2 h-4 w-4" /> Generate Report</Button>
      </div>
      
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h2 className="text-2xl font-bold mb-2">Total Sustainable Finance: ${totalValue}M</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={financialMetrics}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {financialMetrics.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                {financialMetrics.map((metric, index) => (
                  <Line key={metric.name} type="monotone" dataKey={metric.name} stroke={COLORS[index % COLORS.length]} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {financialMetrics.map((metric, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle>{metric.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline">
                <DollarSign className="h-5 w-5 text-gray-400 mr-1" />
                <span className="text-3xl font-bold">{metric.value}</span>
                <span className="text-xl ml-1">{metric.unit}</span>
              </div>
              <div className="flex items-center mt-2">
                <TrendingUp className={`h-4 w-4 ${metric.change > 0 ? 'text-green-500' : 'text-red-500'} mr-1`} />
                <span className={metric.change > 0 ? 'text-green-500' : 'text-red-500'}>
                  {metric.change}% from last year
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showGoalForm ? (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Add New Financial Goal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex space-x-2">
              <Input
                placeholder="Goal Name"
                value={newGoal.name}
                onChange={(e) => setNewGoal({...newGoal, name: e.target.value})}
              />
              <Input
                placeholder="Target Amount (M)"
                type="number"
                value={newGoal.target}
                onChange={(e) => setNewGoal({...newGoal, target: e.target.value})}
              />
              <Button onClick={handleAddGoal}>Add Goal</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button className="mt-6" onClick={() => setShowGoalForm(true)}>
          <PlusCircle className="mr-2 h-4 w-4" /> Add New Financial Goal
        </Button>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="mr-2 h-5 w-5 text-yellow-500" />
            AI-Generated Financial Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 space-y-2">
            <li>Green Bonds have shown the highest growth rate at 15%, indicating strong market demand for environmentally focused investments.</li>
            <li>Consider increasing allocation to ESG-Linked Loans, as they have the lowest growth rate but significant potential for impact.</li>
            <li>The overall sustainable finance portfolio has grown by approximately 12% over the past year, outperforming the general market.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SustainableFinance;